import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X, Settings, Cookie } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const COOKIE_CONSENT_KEY = "cookie-consent";
const COOKIE_PREFERENCES_KEY = "cookie-preferences";

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to prevent layout shift
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPreferences) {
        const prefs = JSON.parse(savedPreferences);
        setPreferences(prefs);
        
        // GA is initialized in index.html based on cookie preferences
      }
    }
  }, []);


  const savePreferences = (prefs: CookiePreferences) => {
    console.log('🍪 Saving cookie preferences:', prefs);
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
    
    if (prefs.analytics) {
      console.log('🔄 Reloading page to initialize Google Analytics');
      window.location.reload();
    }
  };

  const acceptAll = () => {
    savePreferences({
      necessary: true,
      analytics: true,
      marketing: true,
    });
  };

  const rejectAll = () => {
    savePreferences({
      necessary: true,
      analytics: false,
      marketing: false,
    });
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 animate-fade-in">
        <Card className="max-w-4xl mx-auto glass-effect border-primary/20 shadow-2xl">
          <div className="p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary/20 to-primary/10 rounded-xl flex items-center justify-center">
                <Cookie className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              </div>
              
              <div className="flex-1 space-y-4">
                <div>
                  <h3 className="text-lg sm:text-xl font-display font-medium text-foreground mb-2">
                    Wir respektieren Ihre Privatsphäre
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    Diese Website verwendet Cookies, um Ihnen die bestmögliche Erfahrung zu bieten. 
                    Notwendige Cookies sind für die Grundfunktionen der Website erforderlich. 
                    Weitere Informationen finden Sie in unserer{" "}
                    <Link to="/datenschutz" className="text-primary hover:underline font-medium">
                      Datenschutzerklärung
                    </Link>.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    onClick={acceptAll}
                    size="lg"
                    className="font-medium"
                  >
                    Alle akzeptieren
                  </Button>
                  <Button 
                    onClick={rejectAll}
                    variant="outline"
                    size="lg"
                    className="font-medium"
                  >
                    Nur notwendige
                  </Button>
                  <Button 
                    onClick={() => setShowSettings(true)}
                    variant="ghost"
                    size="lg"
                    className="font-medium gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Einstellungen
                  </Button>
                </div>
              </div>

              <button
                onClick={rejectAll}
                className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors p-1"
                aria-label="Banner schließen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </Card>
      </div>

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-display">Cookie-Einstellungen</DialogTitle>
            <DialogDescription className="text-base">
              Wählen Sie, welche Cookies Sie zulassen möchten. Sie können Ihre Einstellungen jederzeit ändern.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Necessary Cookies */}
            <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-muted/50">
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <Label htmlFor="necessary" className="text-base font-medium cursor-pointer">
                    Notwendige Cookies
                  </Label>
                  <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded-full font-medium">
                    Erforderlich
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                </p>
              </div>
              <Switch
                id="necessary"
                checked={true}
                disabled
                className="flex-shrink-0"
              />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-start justify-between gap-4 p-4 rounded-lg hover:bg-muted/30 transition-colors">
              <div className="flex-1 space-y-1">
                <Label htmlFor="analytics" className="text-base font-medium cursor-pointer">
                  Analyse-Cookies
                </Label>
                <p className="text-sm text-muted-foreground">
                  Helfen uns zu verstehen, wie Besucher mit der Website interagieren, um sie zu verbessern.
                </p>
              </div>
              <Switch
                id="analytics"
                checked={preferences.analytics}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, analytics: checked })
                }
                className="flex-shrink-0"
              />
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-start justify-between gap-4 p-4 rounded-lg hover:bg-muted/30 transition-colors">
              <div className="flex-1 space-y-1">
                <Label htmlFor="marketing" className="text-base font-medium cursor-pointer">
                  Marketing-Cookies
                </Label>
                <p className="text-sm text-muted-foreground">
                  Werden verwendet, um Werbung relevanter für Sie und Ihre Interessen zu gestalten.
                </p>
              </div>
              <Switch
                id="marketing"
                checked={preferences.marketing}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, marketing: checked })
                }
                className="flex-shrink-0"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            <Button 
              onClick={saveCustomPreferences}
              className="flex-1 font-medium"
              size="lg"
            >
              Auswahl speichern
            </Button>
            <Button 
              onClick={acceptAll}
              variant="outline"
              className="flex-1 font-medium"
              size="lg"
            >
              Alle akzeptieren
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
